import type { CSSProperties } from "react"

/*
 * Nico, the Nexo mascot, ported from Nexo's web/src/ui/Nico.svelte: the X of
 * the logo with eyes, its four strokes arms and legs turning from the centre.
 * Only what this site uses: the classic colours, the driver's cap, a few
 * moods and the running, waving and cheering animations. The keyframes live
 * in globals.css (nico-*).
 */

const INK = "#1C1A18"
const GREEN = "#1FA05A"
const PURPLE = "#8E44E8"

export type NicoMood = "idle" | "happy" | "wow" | "hurry"
export type NicoAnim = "run" | "wave" | "cheer" | "jump"

type Limb = "ul" | "ur" | "ll" | "lr"
type AnimSpec = { whole?: string; shadow?: string; origin?: string } & Partial<Record<Limb, string>>

// Same timings as Nexo's state/nico.ts; "run" loops for as long as it is set.
const animations: Record<NicoAnim, AnimSpec> = {
  run: {
    whole: "nico-bob .3s ease-in-out infinite",
    ll: "nico-leg .3s ease-in-out infinite",
    lr: "nico-leg .3s ease-in-out infinite reverse",
    ul: "nico-leg .3s ease-in-out infinite reverse",
    ur: "nico-leg .3s ease-in-out infinite",
  },
  wave: { ur: "nico-wave .45s ease-in-out 4 alternate" },
  cheer: {
    whole: "nico-jump .6s ease 2",
    shadow: "nico-shadow .6s ease 2",
    ul: "nico-wave-l .3s ease-in-out 4 alternate",
    ur: "nico-wave .3s ease-in-out 4 alternate",
  },
  jump: { whole: "nico-jump .7s cubic-bezier(.3,.7,.4,1)", shadow: "nico-shadow .7s ease" },
}

const st = (animation: string | undefined, origin: string): CSSProperties => ({
  transformBox: "fill-box",
  transformOrigin: origin,
  ...(animation ? { animation } : {}),
})

const EYES: [number, number][] = [
  [22, 26],
  [42, 26],
]

export function Nico({
  size = 56,
  mood = "idle",
  anim = null,
  playN = 0,
  gaze = [0, 0],
  cap = true,
  shadow = true,
}: {
  size?: number
  mood?: NicoMood
  anim?: NicoAnim | null
  /** Bumped to play a one-shot animation again. */
  playN?: number
  /** Where the pupils look, -3 to 3 each way. */
  gaze?: [number, number]
  cap?: boolean
  shadow?: boolean
}) {
  const A = anim ? animations[anim] : undefined
  const big = mood === "wow" ? 9.5 : 8
  const pr = mood === "wow" ? 3.5 : 4
  const look: [number, number] = mood === "hurry" && gaze[0] === 0 && gaze[1] === 0 ? [3, 0] : gaze

  return (
    <svg
      className="nico block overflow-visible"
      width={size}
      height={(size * 88) / 84}
      viewBox="-10 -18 84 88"
      aria-hidden="true"
    >
      {shadow && (
        <ellipse
          key={`s${playN}`}
          cx="32"
          cy="63"
          rx="17"
          ry="3"
          fill="rgba(28,26,24,0.16)"
          style={st(A?.shadow, "50% 50%")}
        />
      )}
      <g key={`b${playN}-${anim ?? ""}`} style={st(A?.whole, A?.origin ?? "50% 100%")}>
        <g style={st("nico-breathe 3.2s ease-in-out infinite", "50% 100%")}>
          <path d="M32 32L12 12" stroke={GREEN} strokeWidth="13" strokeLinecap="round" fill="none" style={st(A?.ul, "100% 100%")} />
          <path d="M32 32L52 52" stroke={GREEN} strokeWidth="13" strokeLinecap="round" fill="none" style={st(A?.lr, "0% 0%")} />
          <path d="M32 32L52 12" stroke={PURPLE} strokeWidth="13" strokeLinecap="round" fill="none" style={st(A?.ur, "0% 100%")} />
          <path d="M32 32L12 52" stroke={PURPLE} strokeWidth="13" strokeLinecap="round" fill="none" style={st(A?.ll, "100% 0%")} />

          <g style={mood === "happy" ? undefined : st("nico-blink 4.2s ease-in-out infinite", "50% 50%")}>
            {mood === "happy"
              ? EYES.map(([x, y]) => (
                  <path key={x} d={`M${x - 7} ${y + 3}q7-11 14 0`} fill="none" stroke={INK} strokeWidth="3.4" strokeLinecap="round" />
                ))
              : EYES.map(([x, y]) => (
                  <g key={x}>
                    <circle cx={x} cy={y} r={big} fill="#FFFFFF" stroke={INK} strokeWidth="2.5" />
                    <circle
                      cx={x + 1}
                      cy={y + 1}
                      r={pr}
                      fill={INK}
                      style={{ transform: `translate(${look[0]}px,${look[1]}px)`, transition: "transform .18s ease-out" }}
                    />
                  </g>
                ))}
          </g>
          {mood === "happy" && <path d="M27 38q5 5 10 0" fill="none" stroke={INK} strokeWidth="3" strokeLinecap="round" />}
          {mood === "wow" && <ellipse cx="32" cy="41" rx="3.2" ry="4.2" fill={INK} />}
          {mood === "hurry" && (
            <path d="M58 2q4 5 0 8q-4-3 0-8z" fill="#7CC4F5" stroke={INK} strokeWidth="1.5" style={{ animation: "nico-drip 1.2s ease-in infinite" }} />
          )}

          {cap && (
            <g>
              <path d="M12 18q20-22 40 0z" fill="#2563EB" stroke={INK} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M42 17h18q2 3.5-2 4.5H42z" fill="#1E40AF" stroke={INK} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="32" cy="9" r="2.5" fill="#FFD84A" />
            </g>
          )}
        </g>
      </g>
    </svg>
  )
}
